import {useCallback, useMemo, useErrorBoundary} from 'preact/hooks';

import {trackDeliveryPromiseError} from 'utilities/tracking';

import {ReportType, ErrorLoggerInput} from './types';
import {Bugsnag} from './bugsnag';

/**
 * Temp log function, to be replaced with more advanced service
 *
 * @param message
 * @param severity
 * @param parts
 */
function log(
  message: string,
  severity: keyof Pick<Console, 'warn' | 'log' | 'error'>,
  ...parts: unknown[]
) {
  // eslint-disable-next-line no-console
  console[severity].call(null, `[ShopPromise] ${message}`, ...parts);
}

export function useErrorLogger() {
  const reportError = useCallback((input: ErrorLoggerInput) => {
    const {severity, error, message, requestId} = input;

    switch (severity) {
      case ReportType.Investigate:
        Bugsnag.notify(error as Error, {severity: 'error'});
        trackDeliveryPromiseError(
          requestId || '',
          error as Error,
          message || '',
          severity,
        );
        log('Error reported', 'log', error);
        break;
      case ReportType.Metric:
        // todo: Report Observe / Monorail metric
        trackDeliveryPromiseError(
          requestId || '',
          error as Error,
          message || '',
          severity,
        );
        log('Error may impact metrics', 'log', error);
        break;
      case ReportType.Expected:
        trackDeliveryPromiseError(
          requestId || '',
          error as Error,
          message || '',
          severity,
        );
        log(message ?? '', 'warn', error);
        break;
      default:
        log(message ?? '', 'warn', error);
    }
  }, []);

  return useMemo(() => ({reportError}), [reportError]);
}

/**
 *
 * @param scopeName
 */
export function useScopedErrorBoundary(scopeName: string) {
  const {reportError} = useErrorLogger();
  const [error] = useErrorBoundary((error) =>
    reportError({
      severity: ReportType.Investigate,
      error,
      message: scopeName,
    }),
  );

  return error;
}
